import { useUnit } from 'effector-react';
import { Button, Descriptions, Skeleton } from 'antd';
import { CloseOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';
import Chart from 'react-apexcharts';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  $activeMetric,
  $chartsData,
  $cities,
  $floodsArea,
  $hexagons,
  $language,
  $mudfloodsArea,
  $showCharts,
  changeShowChartsEv,
  getChartsDataByCitiesFx,
} from '../../models/logicModel/index.js';
import style from './ChartModal.module.css';
import { addSeparators } from '../../utils/format-utils.js';
import { formatChartData } from '../../utils/chart-utils.js';
import ChartLoading from './ChartLoading.jsx';

const categories = [
  '2016',
  '2017',
  '2018',
  '2019',
  '2020',
  '2021',
  '2022',
  '2023',
  '2024',
  '2025',
];

function ChartModal() {
  const showCharts = useUnit($showCharts);
  const changeShowCharts = useUnit(changeShowChartsEv);
  const hexagons = useUnit($hexagons);
  const data = useUnit($chartsData);
  const cities = useUnit($cities);
  const activeMetric = useUnit($activeMetric);
  const floodsArea = useUnit($floodsArea);
  const mudfloodsArea = useUnit($mudfloodsArea);
  const chartsLoading = useUnit(getChartsDataByCitiesFx.pending);
  const { t } = useTranslation();
  const language = useUnit($language);

  const open = () => {
    changeShowCharts(true);
  };

  const close = () => {
    changeShowCharts(false);
  };

  const population_total = useMemo(() => {
    if (activeMetric === 'flood_ind') {
      return floodsArea.reduce(
        (acc, polygon) => acc + polygon.properties.population,
        0
      );
    }
    if (activeMetric === 'mudflow_ind') {
      return mudfloodsArea.reduce(
        (acc, polygon) => acc + polygon.properties.population,
        0
      );
    }
    return hexagons.reduce(
      (acc, hexagon) => acc + hexagon.properties.zoom9_population,
      0
    );
  }, [hexagons, activeMetric, floodsArea, mudfloodsArea]);

  const buildings_total = useMemo(() => {
    if (activeMetric === 'flood_ind') {
      return floodsArea.reduce(
        (acc, polygon) => acc + polygon.properties.building_cnt,
        0
      );
    }
    if (activeMetric === 'mudflow_ind') {
      return mudfloodsArea.reduce(
        (acc, polygon) => acc + polygon.properties.building_cnt,
        0
      );
    }
    return hexagons.reduce(
      (acc, hexagon) => acc + +hexagon.properties.zoom9_id_building_cnt,
      0
    );
  }, [hexagons, activeMetric, floodsArea, mudfloodsArea]);

  const options = {
    chart: {
      type: 'line',
      height: 350,
      toolbar: {
        show: false,
      },
      zoom: {
        enabled: false,
      },
    },
    dataLabels: {
      enabled: true,
    },
    stroke: {
      curve: 'straight',
    },
    grid: {
      row: {
        colors: ['#f3f3f3', 'transparent'],
        opacity: 0.5,
      },
    },
    xaxis: {
      categories,
    },
  };

  return (
    <>
      <Button
        className={[style.open_button, 'glass'].join(' ')}
        style={{
          display: showCharts ? 'none' : 'block',
        }}
        size="large"
        onClick={open}
        icon={<LeftOutlined />}
        iconPosition="end"
      >
        {t('charts')}
      </Button>
      <div
        className={[
          style.chart_modal,
          'glass',
          showCharts ? style.show : style.hide,
        ].join(' ')}
      >
        <Button
          className={style.close_button}
          onClick={close}
          shape="circle"
          icon={<RightOutlined />}
        />
        <Descriptions
          style={{ marginTop: 30, marginBottom: 10 }}
          bordered
          column={1}
          items={[
            {
              label: t('population'),
              children: addSeparators(population_total),
            },
            {
              label: t('buildings'),
              children: addSeparators(buildings_total),
            },
          ]}
        />
        {chartsLoading ? (
          <ChartLoading />
        ) : (
          <Chart
            options={{
              ...options,
              title: { align: 'left', text: t('vegetation_index') },
            }}
            series={formatChartData(
              data.NDVI,
              cities,
              t('vegetation_index'),
              language
            )}
            type="line"
            height={350}
          />
        )}
        {chartsLoading ? (
          <ChartLoading />
        ) : (
          <Chart
            options={{
              ...options,
              title: { align: 'left', text: t('lst') },
            }}
            series={formatChartData(data.LST, cities, t('lst'), language)}
            type="line"
            height={350}
          />
        )}
        {chartsLoading ? (
          <ChartLoading />
        ) : (
          <Chart
            options={{
              ...options,
              title: {
                align: 'left',
                text: t('lst_dif'),
              },
            }}
            series={formatChartData(
              data['LST-DIF'],
              cities,
              t('lst_dif'),
              language
            )}
            type="line"
            height={350}
          />
        )}
        {chartsLoading ? (
          <ChartLoading />
        ) : (
          <Chart
            options={{
              ...options,
              title: { align: 'left', text: t('air_co') },
            }}
            series={formatChartData(
              data['AIR-CO'],
              cities,
              t('air_co'),
              language
            )}
            type="line"
            height={350}
          />
        )}
        {chartsLoading ? (
          <ChartLoading />
        ) : (
          <Chart
            options={{
              ...options,
              title: { align: 'left', text: t('air_hcho') },
            }}
            series={formatChartData(
              data['AIR-HCHO'],
              cities,
              t('air_hcho'),
              language
            )}
            type="line"
            height={350}
          />
        )}
        {chartsLoading ? (
          <ChartLoading />
        ) : (
          <Chart
            options={{
              ...options,
              title: { align: 'left', text: t('air_no2') },
            }}
            series={formatChartData(
              data['AIR-NO2'],
              cities,
              t('air_no2'),
              language
            )}
            type="line"
            height={350}
          />
        )}
        {chartsLoading ? (
          <ChartLoading />
        ) : (
          <Chart
            options={{
              ...options,
              title: { align: 'left', text: t('air_so2') },
            }}
            series={formatChartData(
              data['AIR-SO2'],
              cities,
              t('air_so2'),
              language
            )}
            type="line"
            height={350}
          />
        )}
      </div>
    </>
  );
}

export default ChartModal;
